import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  ELegalActBox,
  ICreateLegalActRequest,
  ILegalAct,
  ILegalActCoworker,
  ILegalActFileDownload,
  ILegalActListItem,
  ILegalActType,
  ILegalActWorkflowRequest,
  ISaveLegalActBipRequest,
  ISaveLegalActMetricRequest,
  ISaveLegalActPermissionRequest,
  ISaveSupervisoryRulingRequest,
} from "dto/LocalLaw/ILocalLaw";
import {
  ICreatePublicationRequest,
  IPublicationContext,
  IPublicationRequest,
} from "dto/LocalLaw/IPublication";

// Prawo miejscowe — projekty aktów, metryka, obieg akceptacji i czynności biura rady.
//
// Pliki aktu (docx, XML, ZIPX, załączniki, skan rozstrzygnięcia) są NIESZYFROWANE:
// akt prawa miejscowego jest dokumentem publicznym, a XML jest podstawą publikacji.
// Dlatego zakładanie aktu NIE generuje klucza obiektu — inaczej niż przy pismach.

export const localLawQueryKeys = {
  acts: "localLaw.acts",
  act: "localLaw.act",
  actTypes: "localLaw.actTypes",
  publicationContext: "localLaw.publicationContext",
  publicationRequests: "localLaw.publicationRequests",
  coworkers: "localLaw.coworkers",
};

// ── Listy ──────────────────────────────────────────────────────────────────

export interface IGetActsParams {
  box: ELegalActBox;
  type?: number;
  text?: string;
  year?: number;
}

const getActs = (params: IGetActsParams): Promise<{ data: ILegalActListItem[] }> => {
  const search = new URLSearchParams();

  search.set("box", String(params.box));
  if (params.type !== undefined) search.set("type", String(params.type));
  if (params.text) search.set("text", params.text);
  if (params.year !== undefined) search.set("year", String(params.year));

  return axiosInstance.get(`locallaw/acts?${search.toString()}`);
};

const useGetActs = (params: IGetActsParams) =>
  useCustomQuery(
    [localLawQueryKeys.acts, params.box, params.type, params.text, params.year],
    () => getActs(params),
  );

const getAct = (id: string): Promise<{ data: ILegalAct }> =>
  axiosInstance.get(`locallaw/acts/${id}`);

const useGetAct = (id?: string) =>
  useCustomQuery([localLawQueryKeys.act, id], () => getAct(id as string), {
    enabled: !!id,
  });

// ── Cykl życia ─────────────────────────────────────────────────────────────

const createAct = (payload: ICreateLegalActRequest): Promise<{ data: ILegalAct }> =>
  axiosInstance.post("locallaw/acts", payload);

const useCreateAct = () => useCustomMutation(createAct);

const saveMetric = (payload: {
  id: string;
  request: ISaveLegalActMetricRequest;
}): Promise<{ data: ILegalAct }> =>
  axiosInstance.put(`locallaw/acts/${payload.id}/metric`, payload.request);

const useSaveMetric = () => useCustomMutation(saveMetric);

const saveBip = (payload: {
  id: string;
  request: ISaveLegalActBipRequest;
}): Promise<{ data: ILegalAct }> =>
  axiosInstance.put(`locallaw/acts/${payload.id}/bip`, payload.request);

const useSaveBip = () => useCustomMutation(saveBip);

const saveSupervisoryRuling = (payload: {
  id: string;
  request: ISaveSupervisoryRulingRequest;
}): Promise<{ data: ILegalAct }> =>
  axiosInstance.put(`locallaw/acts/${payload.id}/supervisory-ruling`, payload.request);

const useSaveSupervisoryRuling = () => useCustomMutation(saveSupervisoryRuling);

const removeAct = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`locallaw/acts/${id}`);

const useRemoveAct = () => useCustomMutation(removeAct);

// ── Obieg ──────────────────────────────────────────────────────────────────

const workflowCall =
  (action: string) =>
  (payload: {
    id: string;
    request: ILegalActWorkflowRequest;
  }): Promise<{ data: ILegalAct }> =>
    axiosInstance.post(`locallaw/acts/${payload.id}/${action}`, payload.request);

const submit = workflowCall("submit");
const approve = workflowCall("approve");
const sendBack = workflowCall("send-back");
const reject = workflowCall("reject");
const adopt = workflowCall("adopt");
const sendToPublication = workflowCall("to-publish");
const setInForce = workflowCall("in-force");
const revert = workflowCall("revert");

const useSubmit = () => useCustomMutation(submit);
const useApprove = () => useCustomMutation(approve);
const useSendBack = () => useCustomMutation(sendBack);
const useReject = () => useCustomMutation(reject);
const useAdopt = () => useCustomMutation(adopt);
const useSendToPublication = () => useCustomMutation(sendToPublication);
const useSetInForce = () => useCustomMutation(setInForce);
const useRevert = () => useCustomMutation(revert);

// ── Pliki ──────────────────────────────────────────────────────────────────

const generateZipx = (id: string): Promise<{ data: ILegalAct }> =>
  axiosInstance.post(`locallaw/acts/${id}/zipx`);

const useGenerateZipx = () => useCustomMutation(generateZipx);

export interface ILegalActPreview {
  Blob: Blob;
  /** Załączniki pominięte przy składaniu (format nie do przedstawienia na stronie). */
  Skipped: string[];
}

/**
 * Podgląd aktu — PDF złożony po stronie backendu z treści aktu i jego załączników
 * (odpowiednik akt.pdf z Magistratu). Backend składa go na żądanie, więc zawsze
 * odpowiada bieżącej treści dokumentu.
 */
const getPreview = async (id: string): Promise<ILegalActPreview> => {
  const response = await axiosInstance.get(`locallaw/acts/${id}/preview`, {
    responseType: "blob",
  });

  // Nagłówek informacyjny; przy zaostrzonym CORS może go nie być — wtedy po prostu
  // nie pokazujemy ostrzeżenia o pominiętych załącznikach.
  const raw = (response.headers as Record<string, string> | undefined)?.[
    "x-legalact-skipped"
  ];

  return {
    Blob: response.data as unknown as Blob,
    Skipped: raw ? decodeURIComponent(raw).split("|").filter(Boolean) : [],
  };
};

/** Rodzaj pliku aktu: docx (treść), xml (EDAP), zipx (paczka), ruling (rozstrzygnięcie). */
export type LegalActFileKind = "docx" | "xml" | "zipx" | "ruling";

const getFileUrl = (
  id: string,
  kind: LegalActFileKind,
): Promise<{ data: ILegalActFileDownload }> =>
  axiosInstance.get(`locallaw/acts/${id}/file/${kind}/url`);

/**
 * Pobiera plik aktu przez API (nie presigned URL), żeby przeglądarka zapisała go
 * pod właściwą nazwą — klucz w repozytorium to 128 losowych znaków.
 */
const downloadFile = async (id: string, kind: LegalActFileKind, fileName?: string) => {
  const response = await axiosInstance.get(`locallaw/acts/${id}/file/${kind}`, {
    responseType: "blob",
  });

  const url = window.URL.createObjectURL(response.data as Blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName ?? `akt.${kind === "zipx" ? "zipx" : kind}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ── Wnioski o publikację (UPE) ─────────────────────────────────────────────

const getPublicationContext = (): Promise<{ data: IPublicationContext }> =>
  axiosInstance.get("locallaw/publication/context");

// Kontekst kreatora sięga na żywo do UPE (nadawca, adresaci), więc bywa wolny
// i nie ma sensu trzymać go w pamięci podręcznej między otwarciami kreatora.
const useGetPublicationContext = (enabled: boolean) =>
  useCustomQuery([localLawQueryKeys.publicationContext], getPublicationContext, {
    enabled,
    cacheTime: 0,
    staleTime: 0,
  });

const getPublicationRequests = (): Promise<{ data: IPublicationRequest[] }> =>
  axiosInstance.get("locallaw/publication/requests");

const useGetPublicationRequests = (enabled = true) =>
  useCustomQuery([localLawQueryKeys.publicationRequests], getPublicationRequests, {
    enabled,
  });

const createPublicationRequest = (
  payload: ICreatePublicationRequest,
): Promise<{ data: IPublicationRequest }> =>
  axiosInstance.post("locallaw/publication/requests", payload);

const useCreatePublicationRequest = () => useCustomMutation(createPublicationRequest);

const sendPublicationRequest = (id: string): Promise<{ data: IPublicationRequest }> =>
  axiosInstance.post(`locallaw/publication/requests/${id}/send`);

const useSendPublicationRequest = () => useCustomMutation(sendPublicationRequest);

/** Rodzaj pliku wniosku: xml (Wniosek.xml) albo package (Wniosek.zip). */
export type PublicationFileKind = "xml" | "package";

const getRequestFileUrl = (
  id: string,
  kind: PublicationFileKind,
): Promise<{ data: ILegalActFileDownload }> =>
  axiosInstance.get(`locallaw/publication/requests/${id}/file/${kind}/url`);

// ── Słowniki i uprawnienia ─────────────────────────────────────────────────

const getActTypes = (): Promise<{ data: ILegalActType[] }> =>
  axiosInstance.get("locallaw/act-types");

const useGetActTypes = () =>
  useCustomQuery([localLawQueryKeys.actTypes], getActTypes, {
    // Słownik rodzajów aktów nie zmienia się w trakcie sesji.
    staleTime: Infinity,
  });

const getCoworkers = (): Promise<{ data: ILegalActCoworker[] }> =>
  axiosInstance.get("locallaw/coworkers");

const useGetCoworkers = () =>
  useCustomQuery([localLawQueryKeys.coworkers], getCoworkers);

const savePermission = (
  payload: ISaveLegalActPermissionRequest,
): Promise<{ data: ILegalActCoworker[] }> =>
  axiosInstance.post("locallaw/permissions", payload);

const useSavePermission = () => useCustomMutation(savePermission);

const localLawService = {
  getActs,
  useGetActs,
  getAct,
  useGetAct,

  createAct,
  useCreateAct,
  saveMetric,
  useSaveMetric,
  saveBip,
  useSaveBip,
  saveSupervisoryRuling,
  useSaveSupervisoryRuling,
  removeAct,
  useRemoveAct,

  useSubmit,
  useApprove,
  useSendBack,
  useReject,
  useAdopt,
  useSendToPublication,
  useSetInForce,
  useRevert,

  generateZipx,
  useGenerateZipx,
  getPreview,
  getFileUrl,
  downloadFile,

  getPublicationContext,
  useGetPublicationContext,
  getPublicationRequests,
  useGetPublicationRequests,
  createPublicationRequest,
  useCreatePublicationRequest,
  sendPublicationRequest,
  useSendPublicationRequest,
  getRequestFileUrl,

  getActTypes,
  useGetActTypes,
  getCoworkers,
  useGetCoworkers,
  savePermission,
  useSavePermission,
};

export default localLawService;
